import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Clock, Users, Globe, ExternalLink, 
  CheckCircle2, Circle, Loader2, BookOpen, ShoppingBasket
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const WebRecipePage = () => {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [cookingMode, setCookingMode] = useState(false);

  useEffect(() => {
    // Get recipe from sessionStorage
    const storedRecipe = sessionStorage.getItem('webRecipe');
    if (storedRecipe) {
      try {
        const parsed = JSON.parse(storedRecipe);
        if (parsed.id === recipeId) {
          setRecipe(parsed);
        } else {
          navigate('/fridge-scanner');
        }
      } catch (e) {
        navigate('/fridge-scanner');
      }
    } else {
      navigate('/fridge-scanner');
    }
    setLoading(false);
  }, [recipeId, navigate]);

  const toggleStep = (stepOrder) => {
    setCompletedSteps(prev => 
      prev.includes(stepOrder) 
        ? prev.filter(s => s !== stepOrder)
        : [...prev, stepOrder]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <Globe className="w-16 h-16 text-muted-foreground/30 mb-4" />
        <p className="text-muted-foreground">Recette non trouvée</p>
        <Link to="/fridge-scanner">
          <Button className="mt-4 rounded-full">Retour au scanner</Button>
        </Link>
      </div>
    );
  }

  const progress = recipe.steps?.length > 0 
    ? Math.round((completedSteps.length / recipe.steps.length) * 100) 
    : 0;

  return (
    <div className="min-h-screen pb-24">
      {/* Header with Image */}
      <div className="relative">
        {recipe.image_url ? (
          <div className="h-64 md:h-80 w-full">
            <img 
              src={recipe.image_url} 
              alt={recipe.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        ) : (
          <div className="h-48 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
            <Globe className="w-16 h-16 text-cyan-500/40" />
          </div>
        )}
        
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 p-2 rounded-full bg-black/30 backdrop-blur-sm text-white hover:bg-black/50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        {/* Source Badge */}
        <div className="absolute top-4 right-4">
          <Badge className="bg-cyan-500 text-white gap-1">
            <GustoIcon className="w-4 h-4" />
            {recipe.source_name || "Chef Gusto"}
          </Badge>
        </div>
      </div>

      {/* Recipe Info */}
      <div className="px-4 -mt-8 relative z-10">
        <Card className="shadow-xl">
          <CardContent className="p-4">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-charcoal mb-2">
              {recipe.name}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              {recipe.prep_time && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>Prépa: {recipe.prep_time}</span>
                </div>
              )}
              {recipe.cook_time && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-orange-500" />
                  <span>Cuisson: {recipe.cook_time}</span>
                </div>
              )}
              {recipe.servings && (
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{recipe.servings}</span>
                </div>
              )}
            </div>

            {/* Source Link - only show if there's a URL */}
            {recipe.source_url && (
              <a 
                href={recipe.source_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-cyan-600 hover:text-cyan-700 mt-3"
              >
                <ExternalLink className="w-4 h-4" />
                Voir sur {recipe.source_name}
              </a>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Content */}
      <div className="px-4 mt-6 space-y-6">
        {/* Ingredients Section */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <ShoppingBasket className="w-5 h-5 text-cyan-600" />
            <h2 className="font-heading text-lg font-semibold">
              Ingrédients
            </h2>
            {recipe.ingredients?.length > 0 && (
              <Badge variant="outline">{recipe.ingredients.length}</Badge>
            )}
          </div>

          <Card>
            <CardContent className="p-4">
              {recipe.ingredients?.length > 0 ? (
                <ul className="space-y-2">
                  {recipe.ingredients.map((ing, index) => (
                    <li key={index} className="flex items-start gap-3 py-1">
                      <span className="w-2 h-2 rounded-full bg-cyan-500 mt-2 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-charcoal">{ing.name}</span>
                        {(ing.quantity || ing.unit) && (
                          <span className="text-muted-foreground ml-2">
                            {ing.quantity} {ing.unit}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  Aucun ingrédient disponible
                </p>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Cooking Mode Toggle */}
        {recipe.steps?.length > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-cyan-600" />
              <h2 className="font-heading text-lg font-semibold">Préparation</h2>
              <Badge variant="outline">{recipe.steps.length} étapes</Badge>
            </div>
            
            <Button
              variant={cookingMode ? "default" : "outline"}
              size="sm"
              onClick={() => setCookingMode(!cookingMode)}
              className={cookingMode ? "bg-cyan-500 hover:bg-cyan-600" : ""}
            >
              <GustoIcon className="w-4 h-4" />
              {cookingMode ? "Mode cuisine activé" : "Mode cuisine"}
            </Button>
          </div>
        )}

        {/* Progress Bar in Cooking Mode */}
        {cookingMode && recipe.steps?.length > 0 && (
          <Card className="bg-cyan-50 border-cyan-200">
            <CardContent className="p-3">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-cyan-700 font-medium">Progression</span>
                <span className="text-cyan-600">{completedSteps.length}/{recipe.steps.length} étapes</span>
              </div>
              <div className="h-2 bg-cyan-200 rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-cyan-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Steps Section */}
        <section>
          {recipe.steps?.length > 0 ? (
            <div className="space-y-4">
              {recipe.steps.map((step, index) => {
                const isCompleted = completedSteps.includes(step.order);
                
                return (
                  <motion.div
                    key={step.order}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card 
                      className={`transition-all ${
                        cookingMode 
                          ? isCompleted 
                            ? 'bg-green-50 border-green-200' 
                            : 'cursor-pointer hover:shadow-md'
                          : ''
                      }`}
                      onClick={() => cookingMode && toggleStep(step.order)}
                    >
                      <CardContent className="p-4">
                        <div className="flex gap-3">
                          {/* Step Number or Check */}
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                            isCompleted 
                              ? 'bg-green-500 text-white' 
                              : 'bg-cyan-100 text-cyan-700'
                          }`}>
                            {isCompleted ? (
                              <CheckCircle2 className="w-5 h-5" />
                            ) : (
                              step.order
                            )}
                          </div>
                          
                          {/* Step Description */}
                          <p className={`text-charcoal leading-relaxed flex-1 ${
                            isCompleted ? 'text-muted-foreground line-through' : ''
                          }`}>
                            {step.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">
                Instructions non disponibles
              </p>
              {recipe.source_url && (
                <a 
                  href={recipe.source_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-cyan-600 hover:text-cyan-700 mt-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Voir la recette complète sur {recipe.source_name}
                </a>
              )}
            </Card>
          )}
        </section>

        {/* Completion Message */}
        {cookingMode && completedSteps.length === recipe.steps?.length && recipe.steps?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <Card className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
              <CardContent className="p-6 text-center">
                <GustoLogo size="lg" />
                <h3 className="text-xl font-bold mb-2">Bravo, chef ! 🎉</h3>
                <p className="text-white/90">Vous avez terminé cette recette</p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="w-full h-12 rounded-2xl gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          Retour
        </Button>
      </div>
    </div>
  );
};

export default WebRecipePage;
